import { useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTenantAuth } from "../../context/TeanantAuthProvider";
import { useTenantLogout } from "../../hooks/tenant/useTenantLogout";
import LogoutModal from "./LogoutModal";

/**
 * @typedef {Object} MenuItem
 * @property {string} label - Display text for the menu item
 * @property {string} [link] - Navigation link (optional if onClick is provided)
 * @property {string} [icon] - Bootstrap icon class (e.g., "bi-gear")
 * @property {() => void} [onClick] - Custom click handler (optional)
 * @property {boolean} [isDanger] - If true, shows in red (for destructive actions)
 * @property {boolean} [showDividerBefore] - If true, shows a divider before this item
 */

/**
 * @typedef {Object} ProfileDropdownProps
 * @property {MenuItem[]} [menuItems] - Array of menu items to display
 * @property {string} [loginRedirectPath] - Path to redirect after logout (default: "/login")
 */

/**
 * A reusable profile dropdown component with logout functionality.
 * Can be used across different dashboards with custom menu items.
 */

const ProfileDropdown = ({ menuItems = [], loginRedirectPath = "/login" }) => {
  const navigate = useNavigate();
  const { currentUser } = useTenantAuth();
  const { tenantLogout, loading, success, error, resetState } =
    useTenantLogout();
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const getInitials = (name) => {
    if (!name) return "YQ";
    const parts = name.trim().split(" ");
    if (parts.length > 1) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleLogoutClick = () => {
    resetState();
    setShowLogoutModal(true);
  };

  const handleCloseModal = () => {
    setShowLogoutModal(false);
    resetState();
  };

  const handleMenuItemClick = (item) => {
    if (item.onClick) {
      item.onClick();
    } else if (item.link) {
      navigate(item.link);
    }
  };

  if (!currentUser) {
    return <div className="navbar-header-brand">Welcome</div>;
  }

  return (
    <>
      <Dropdown align="end">
        <Dropdown.Toggle
          variant="secondary"
          id="profile-dropdown"
          className="profile-dropdown-toggle"
        >
          {getInitials(currentUser.name)}
        </Dropdown.Toggle>

        <Dropdown.Menu className="profile-dropdown-menu">
          {/* User Info */}
          <div className="dropdown-user-info px-3 py-2">
            <div className="fw-bold">{currentUser.name}</div>
            <div
              className="text-muted small text-truncate"
              style={{ maxWidth: "300px" }}
              title={currentUser.email}
            >
              {currentUser.email}
            </div>
          </div>
          <Dropdown.Divider />

          {/* Custom Menu Items */}
          {menuItems.map((item, index) => (
            <div key={index}>
              {item.showDividerBefore && <Dropdown.Divider />}
              <Dropdown.Item
                onClick={() => handleMenuItemClick(item)}
                className={item.isDanger ? "text-danger" : ""}
              >
                {item.icon && <i className={`bi ${item.icon} me-2`}></i>}
                {item.label}
              </Dropdown.Item>
            </div>
          ))}

          {/* Logout (always shown) */}
          {menuItems.length > 0 && <Dropdown.Divider />}
          <Dropdown.Item onClick={handleLogoutClick} className="text-danger">
            <i className="bi bi-box-arrow-right me-2"></i>
            Logout
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>

      {/* Logout Modal */}
      <LogoutModal
        showModal={showLogoutModal}
        handleClose={handleCloseModal}
        onLogout={tenantLogout}
        loading={loading}
        success={success}
        error={error}
        redirectPath={loginRedirectPath}
      />
    </>
  );
};

export default ProfileDropdown;
